import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, ClientApiError } from '../../client/api'
import {
  applyCountryDialToPhone,
  dialForCountry,
} from '../../client/registerCountries'
import { portalLinks } from '../portal-links'
import { REGISTER_RISK_NOTICE, RISK_LINKS } from '../risk-copy'
import {
  trackCreateAccountError,
  trackCreateAccountFocus,
  trackCreateAccountSubmit,
  trackCreateAccountSuccess,
  trackRiskDisclosureClicked,
} from '../../analytics/track'
import { REGISTER_EMAIL_RE } from './trade-landing-config'
import { TradeCountrySelect } from './TradeCountrySelect'
import { GoogleSignInButton } from '../../client/components/GoogleSignInButton'

type FieldErrors = Partial<Record<'firstName' | 'lastName' | 'email' | 'password' | 'form', string>>

function validate(values: {
  firstName: string
  lastName: string
  email: string
  password: string
}): FieldErrors {
  const errors: FieldErrors = {}
  if (!values.firstName.trim()) errors.firstName = 'Enter your first name.'
  else if (values.firstName.trim().length > 64) errors.firstName = 'First name must be 64 characters or fewer.'
  if (!values.lastName.trim()) errors.lastName = 'Enter your last name.'
  else if (values.lastName.trim().length > 64) errors.lastName = 'Last name must be 64 characters or fewer.'
  if (!values.email.trim()) errors.email = 'Enter your email address.'
  else if (!REGISTER_EMAIL_RE.test(values.email.trim()) || values.email.includes('..')) {
    errors.email = 'Enter a valid email address.'
  }
  if (!values.password) errors.password = 'Enter a password of at least 8 characters.'
  else if (values.password.length < 8) errors.password = 'Password must be at least 8 characters.'
  else if (values.password.length > 128) errors.password = 'Password must be 128 characters or fewer.'
  return errors
}

function useMobileLanding() {
  const [mobile, setMobile] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const sync = () => setMobile(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])
  return mobile
}

type Props = {
  onRegistered?: (info: { maskedEmail: string; devToken: string }) => void
}

export function TradeCheckEmailCard({
  maskedEmail,
  devToken,
}: {
  maskedEmail: string
  devToken: string
}) {
  return (
    <aside className="tl-reg tl-reg--done" id="create-account" aria-labelledby="tl-reg-title">
      <p className="tl-reg__badge">Perfect Predict Portal + MetaTrader 5</p>
      <h2 id="tl-reg-title" className="tl-reg__title" tabIndex={-1}>
        Check your email
      </h2>
      <p className="tl-reg__lead">
        Account created. Open the verification link we sent to {maskedEmail} to continue. Email
        verification is required.
      </p>
      {devToken ? (
        <p className="tl-reg__note">
          Local/dev token ready —{' '}
          <a
            href={`${portalLinks.home.replace(/\/app$/, '')}/app/verify-email?token=${encodeURIComponent(devToken)}`}
          >
            verify now
          </a>
        </p>
      ) : null}
      <a className="wt-btn wt-btn--primary tl-reg__submit" href={portalLinks.login}>
        Continue to sign in
      </a>
    </aside>
  )
}

export function TradeLandingRegisterForm({ onRegistered }: Props) {
  const mobile = useMobileLanding()
  const [searchParams] = useSearchParams()
  const ibCode = (searchParams.get('ib') || searchParams.get('ref') || '').trim().toUpperCase()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [country, setCountry] = useState('')
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<FieldErrors>({})
  const focusedOnce = useRef(false)

  const consentParts = useMemo(() => REGISTER_RISK_NOTICE.split(/(Terms|Privacy Policy|Risk Disclosure)/), [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    const nextErrors = validate({ firstName, lastName, email, password })
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors)
      trackCreateAccountError('hero')
      return
    }
    setBusy(true)
    setErrors({})
    trackCreateAccountSubmit('hero')
    try {
      const res = await api.register({
        email: email.trim(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || '',
        country: country.trim() || '',
        ib_code: ibCode || undefined,
      })
      if (!res.success) {
        setErrors({ form: 'Registration failed. Please try again.' })
        trackCreateAccountError('hero')
        return
      }
      trackCreateAccountSuccess('hero')
      onRegistered?.({
        maskedEmail: res.email || 'your email',
        devToken: res.devVerificationToken || '',
      })
    } catch (err) {
      const message =
        err instanceof ClientApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Registration failed. Please try again.'
      setErrors({ form: message })
      trackCreateAccountError('hero')
    } finally {
      setBusy(false)
    }
  }

  async function onGoogle(credential: string) {
    if (busy) return
    setBusy(true)
    setErrors({})
    trackCreateAccountSubmit('hero')
    try {
      const res = await api.loginWithGoogle(credential, ibCode)
      if (!res.accessToken) {
        setErrors({ form: 'Google sign-in failed. Please try again.' })
        trackCreateAccountError('hero')
        return
      }
      trackCreateAccountSuccess('hero')
      const dest = `${portalLinks.home}#pp_handoff=${encodeURIComponent(res.accessToken)}`
      window.location.assign(dest)
    } catch (err) {
      const message =
        err instanceof ClientApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Google sign-in failed. Please try again.'
      setErrors({ form: message })
      trackCreateAccountError('hero')
      setBusy(false)
    }
  }

  return (
    <aside className="tl-reg" id="create-account" aria-labelledby="tl-reg-title">
      <p className="tl-reg__badge">Perfect Predict Portal + MetaTrader 5</p>
      <h2 id="tl-reg-title" className="tl-reg__title">
        <span className="tl-reg__title-d">Create Account</span>
        <span className="tl-reg__title-m">Create Your Perfect Predict Account</span>
      </h2>
      <p className="tl-reg__lead">
        Create one account for the Perfect Predict Portal and MT5. Use Google, or email — email
        verification is required.
        {ibCode ? (
          <>
            {' '}
            Partner referral: <strong>{ibCode}</strong>
          </>
        ) : null}
      </p>

      {errors.form ? (
        <p className="tl-reg__alert" role="alert" aria-live="polite">
          {errors.form}
        </p>
      ) : null}

      <div className="tl-reg__google">
        <GoogleSignInButton
          mode="signup"
          disabled={busy}
          divider="below"
          onCredential={onGoogle}
          onError={(message) => {
            setErrors({ form: message })
            trackCreateAccountError('hero')
          }}
        />
      </div>

      <form
        className="tl-reg__form ph-mask"
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        onFocusCapture={() => {
          if (focusedOnce.current) return
          focusedOnce.current = true
          trackCreateAccountFocus('hero')
        }}
      >
        <div className="tl-reg__row">
          <label className="tl-field">
            <span className="tl-field__label">First Name</span>
            <input
              id="trade-first-name"
              name="first_name"
              className="tl-input"
              autoComplete="given-name"
              placeholder={mobile ? 'First Name' : undefined}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              maxLength={64}
              aria-invalid={errors.firstName ? true : undefined}
              aria-describedby="trade-first-name-error"
            />
            <span id="trade-first-name-error" className="tl-field__error">{errors.firstName || ''}</span>
          </label>
          <label className="tl-field">
            <span className="tl-field__label">Last Name</span>
            <input
              id="trade-last-name"
              name="last_name"
              className="tl-input"
              autoComplete="family-name"
              placeholder={mobile ? 'Last Name' : undefined}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              maxLength={64}
              aria-invalid={errors.lastName ? true : undefined}
              aria-describedby="trade-last-name-error"
            />
            <span id="trade-last-name-error" className="tl-field__error">{errors.lastName || ''}</span>
          </label>
        </div>

        <label className="tl-field">
          <span className="tl-field__label">Email</span>
          <input
            id="trade-email"
            name="email"
            type="email"
            className="tl-input"
            autoComplete="email"
            placeholder={mobile ? 'Email' : undefined}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-invalid={errors.email ? true : undefined}
            aria-describedby="trade-email-error"
          />
          <span id="trade-email-error" className="tl-field__error">{errors.email || ''}</span>
        </label>

        <div className="tl-field">
          <span className="tl-field__label" id="trade-password-label">
            Password
          </span>
          <div className="tl-password">
            <input
              id="trade-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              className="tl-input"
              autoComplete="new-password"
              placeholder={mobile ? 'Password' : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              maxLength={128}
              aria-labelledby="trade-password-label"
              aria-invalid={errors.password ? true : undefined}
              aria-describedby="trade-password-error"
            />
            <button
              type="button"
              className="tl-password__toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <span id="trade-password-error" className="tl-field__error" role={errors.password ? 'alert' : undefined}>{errors.password || ''}</span>
        </div>

        <div className="tl-reg__row">
          <div className="tl-field">
            <label className="tl-field__label" htmlFor="trade-country">
              Country
            </label>
            <TradeCountrySelect
              id="trade-country"
              placeholder={mobile ? 'Country' : 'Search country'}
              value={country}
              onChange={(next) => {
                setCountry(next)
                const dial = dialForCountry(next)
                if (dial) setPhone((prev) => applyCountryDialToPhone(prev, dial))
              }}
            />
          </div>
          <label className="tl-field">
            <span className="tl-field__label">Phone</span>
            <input
              id="trade-phone"
              name="phone"
              type="tel"
              className="tl-input"
              autoComplete="tel"
              inputMode="tel"
              placeholder={mobile ? 'Phone' : '+971 …'}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
        </div>

        <p className="tl-reg__consent">
          {consentParts.map((part, i) => {
            if (part === 'Terms') {
              return (
                <a key={i} href={RISK_LINKS.terms} target="_blank" rel="noreferrer">
                  Terms
                </a>
              )
            }
            if (part === 'Privacy Policy') {
              return (
                <a key={i} href={RISK_LINKS.privacy} target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
              )
            }
            if (part === 'Risk Disclosure') {
              return (
                <a
                  key={i}
                  href={RISK_LINKS.disclosure}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackRiskDisclosureClicked('hero')}
                >
                  Risk Disclosure
                </a>
              )
            }
            return <span key={i}>{part}</span>
          })}
        </p>

        <button
          type="submit"
          className="wt-btn wt-btn--primary tl-reg__submit"
          disabled={busy}
          aria-busy={busy}
        >
          {busy ? 'Creating Account…' : 'Create Account'}
        </button>
      </form>
    </aside>
  )
}
